// ========================================================================
// SocialOS SDK - Error Handling & Logging
// ========================================================================

import { ApiError, ApiResponse, PaginatedResponse } from './types';

// ============ Custom Error Classes ============

export class SdkError extends Error {
  code: string;
  status?: number;
  details?: Record<string, unknown>;
  originalError?: unknown;

  constructor(apiError: ApiError | string, originalError?: unknown) {
    const error = typeof apiError === 'string' 
      ? { code: 'UNKNOWN_ERROR', message: apiError }
      : apiError;
    
    super(error.message);
    this.name = 'SdkError';
    this.code = error.code;
    this.status = error.status;
    this.details = error.details;
    this.originalError = originalError;
  }
}

export class NetworkError extends SdkError {
  constructor(message = 'Network request failed', originalError?: unknown) {
    super({ code: 'NETWORK_ERROR', message }, originalError);
    this.name = 'NetworkError';
  }
}

export class AuthError extends SdkError {
  constructor(message = 'Authentication failed', originalError?: unknown) {
    super({ code: 'AUTH_ERROR', message }, originalError);
    this.name = 'AuthError';
  }
}

export class OfflineError extends SdkError {
  constructor(message = 'Operation queued for offline sync') {
    super({ code: 'OFFLINE_OPERATION', message });
    this.name = 'OfflineError';
  }
}

export class TimeoutError extends SdkError {
  constructor(timeout: number) {
    super({ 
      code: 'TIMEOUT_ERROR', 
      message: `Request timed out after ${timeout}ms` 
    });
    this.name = 'TimeoutError';
  }
}

// ============ Error Handler ============

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorListeners: Array<(error: SdkError) => void> = [];

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  handle(error: unknown): SdkError {
    const sdkError = this.normalizeError(error);
    this.notifyListeners(sdkError);
    this.logError(sdkError);
    return sdkError;
  }

  private normalizeError(error: unknown): SdkError {
    if (error instanceof SdkError) {
      return error;
    }

    const err = error as Record<string, unknown>;
    const errName = err.name as string | undefined;
    const errMessage = err.message as string | undefined;

    if (errName === 'AbortError') {
      return new TimeoutError(30000);
    }

    if (errName === 'TypeError' && typeof errMessage === 'string' && errMessage.includes('fetch')) {
      return new NetworkError(errMessage, error);
    }

    if (err.response) {
      const resp = err.response as Record<string, unknown>;
      const apiError: ApiError = {
        code: (resp.code as string) || 'API_ERROR',
        message: (resp.message as string) || errMessage || 'Request failed',
        status: resp.status as number | undefined,
        details: resp.details as Record<string, unknown> | undefined
      };
      return new SdkError(apiError, error);
    }

    return new SdkError({
      code: 'UNKNOWN_ERROR',
      message: errMessage || 'An unknown error occurred',
      status: err.status as number | undefined
    }, error);
  }

  private notifyListeners(error: SdkError): void {
    this.errorListeners.forEach((listener) => {
      try {
        listener(error);
      } catch (e) {
        console.error('Error in error listener:', e);
      }
    });
  }

  private logError(error: SdkError): void {
    const logEntry = {
      timestamp: new Date().toISOString(),
      name: error.name,
      code: error.code,
      message: error.message,
      status: error.status,
      details: error.details,
      stack: error.stack
    };

    if (process.env.NODE_ENV === 'development') {
      console.error('[SDK Error]', logEntry);
    }

    // Send to error tracking service in production
    if (process.env.NODE_ENV === 'production') {
      this.sendToErrorTracking(logEntry);
    }
  }

  private sendToErrorTracking(logEntry: Record<string, unknown>): void {
    // Placeholder for error tracking integration (Sentry, etc.)
    const win = window as Window & { trackError?: (entry: Record<string, unknown>) => void };
    if (typeof window !== 'undefined' && win.trackError) {
      win.trackError(logEntry);
    }
  }

  addListener(listener: (error: SdkError) => void): () => void {
    this.errorListeners.push(listener);
    return () => {
      this.errorListeners = this.errorListeners.filter((l) => l !== listener);
    };
  }

  clearListeners(): void {
    this.errorListeners = [];
  }
}

// ============ Response Validator ============

export class ResponseValidator {
  static validate<T>(response: Response): Promise<ApiResponse<T>> {
    return response.json()
      .then(data => {
        if (!response.ok) {
          throw {
            code: data.code || 'API_ERROR',
            message: data.message || 'Request failed',
            status: response.status,
            details: data.details
          };
        }
        return {
          success: true,
          data: data.data || data
        };
      })
      .catch(error => {
        throw error;
      });
  }

  static validatePaginated<T>(response: Response): Promise<PaginatedResponse<T>> {
    return response.json()
      .then(data => {
        if (!response.ok) {
          throw {
            code: data.code || 'API_ERROR',
            message: data.message || 'Request failed',
            status: response.status
          };
        }
        return {
          data: data.data || [],
          count: data.count || 0,
          page: data.page || 1,
          limit: data.limit || 20,
          totalPages: data.totalPages || 1
        };
      });
  }
}

// ============ Error Utilities ============

export const isNetworkError = (error: unknown): boolean => {
  if (error instanceof NetworkError) return true;
  const err = error as Record<string, unknown>;
  return err.name === 'TypeError' && typeof err.message === 'string' && err.message.includes('fetch');
};

export const isAuthError = (error: unknown): boolean => {
  if (error instanceof AuthError) return true;
  const err = error as Record<string, unknown>;
  return err.code === 'AUTH_ERROR';
};

export const isOfflineError = (error: unknown): boolean => {
  if (error instanceof OfflineError) return true;
  const err = error as Record<string, unknown>;
  return err.code === 'OFFLINE_OPERATION';
};

export const isTimeoutError = (error: unknown): boolean => {
  if (error instanceof TimeoutError) return true;
  const err = error as Record<string, unknown>;
  return err.code === 'TIMEOUT_ERROR';
};

export const shouldRetry = (error: unknown, retryConfig: { retryableStatuses: number[] }): boolean => {
  if ((error as { status?: number }).status && retryConfig.retryableStatuses.includes((error as { status: number }).status)) {
    return true;
  }
  return isNetworkError(error) || isTimeoutError(error);
};
